/**
import { getVariantUrl } from '../../photos/AdaptivePhoto';
 * CollectionEditTemplate - Template for editing collection items
 *
 * Displays photo with inline text editing below. Supports:
 * - Photo display (clickable for selection) with aspect ratio preservation
 * - Text editor (separate click zone with stopPropagation)
 * - Blur-to-save with 500ms debounce
 * - Enter to save, Shift+Enter for newline
 * - Character limit (2000 chars) with counter
 * - Selection indicator (ring when isSelected)
 *
 * Note: Drag-drop reordering is handled automatically by PhotoCollection2
 * when reorderMode={true}. No custom drag-drop code needed here.
 *
 * @module PhotoItem2/templates/CollectionEditTemplate
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { useCollectionEditContext } from '@/contexts/CollectionEditContext';
import type { PhotoItem2TemplateProps } from '../PhotoItem2.types';
import { AdaptivePhoto } from '../../photos/AdaptivePhoto';
import { SelectionIndicator } from '@/components/atoms';
import logger from '@/utils/logger';

const MAX_TEXT_LENGTH = 2000;

/**
 * CollectionEditTemplate component
 *
 * Renders photo with text editing capabilities for collection editing.
 * Uses separate click zones to prevent selection conflicts with text editing.
 * Maintains aspect ratio and fits well in grid layouts like PhotoOnlyTemplate.
 */
export function CollectionEditTemplate({
  data,
  isSelected = false,
  selectable = false,
  zoom = 1.0, // NOTE: zoom prop no longer used - AdaptivePhoto uses container size
  onClick,
  onCritiqueClick: _onCritiqueClick, // Filter out non-DOM prop
  className,
  ...restProps
}: PhotoItem2TemplateProps) {
  void zoom; // Suppress unused variable warning - AdaptivePhoto uses container size instead
  void _onCritiqueClick; // Filter out - not a valid DOM prop

  const { onUpdateText } = useCollectionEditContext();
  const [isEditingText, setIsEditingText] = useState(false);
  const [textValue, setTextValue] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const collectionData = data;

  // Initialize text value from data
  useEffect(() => {
    setTextValue(collectionData.textContent || '');
  }, [collectionData.textContent]);

  // Auto-focus textarea when entering edit mode
  useEffect(() => {
    if (isEditingText && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditingText]);

  // Cleanup save timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const charCount = textValue.length;
  const isOverLimit = charCount > MAX_TEXT_LENGTH;

  const handleSaveText = useCallback(async () => {
    // Don't save if over limit or already saving
    if (isOverLimit || isSaving) return;

    // Don't save if text hasn't changed
    const trimmedText = textValue.trim();
    if (trimmedText === (collectionData.textContent?.trim() || '')) {
      setIsEditingText(false);
      return;
    }

    try {
      setIsSaving(true);
      await onUpdateText?.(collectionData.photoData.id!, trimmedText || undefined);
      setIsEditingText(false);
    } catch (error) {
      logger.error('CollectionEditTemplate', '[CollectionEditTemplate] Failed to save text:', error);
      // Revert to previous value on error
      setTextValue(collectionData.textContent || '');
      setIsEditingText(false);
    } finally {
      setIsSaving(false);
    }
  }, [isOverLimit, isSaving, textValue, collectionData.textContent, collectionData.photoData.id, onUpdateText]);

  const handleTextBlur = useCallback(() => {
    // Debounce the save by 500ms
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      handleSaveText();
    }, 500);
  }, [handleSaveText]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Stop propagation to prevent dnd-kit KeyboardSensor from capturing keys (e.g., space for drag)
    e.stopPropagation();

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      handleSaveText();
    } else if (e.key === 'Escape') {
      // Cancel editing and revert
      setTextValue(collectionData.textContent || '');
      setIsEditingText(false);
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    }
  }, [handleSaveText, collectionData.textContent]);

  // Note: Error handling for missing image URLs is handled by AdaptivePhoto component

  // Feature 081: photo-versioning - Forward additional props (e.g., onContextMenu) to div
  return (
    <div
      className={cn(
        'relative flex flex-col overflow-hidden rounded-lg bg-surface-2 w-full h-full',
        isSelected && 'ring-4 ring-primary',
        className
      )}
      {...restProps}
    >
      {/* Photo area - fills available space, clickable for selection */}
      <div
        onClick={onClick}
        className={cn(
          'relative flex-1 flex items-center justify-center overflow-hidden select-none',
          selectable && 'cursor-pointer hover:opacity-90'
        )}
      >
        {/* Adaptive photo with automatic resolution selection */}
        <AdaptivePhoto
          photo={data.photoData}
          alt={data.photoData.description || ''}
          className="w-full h-full p-0.5"
        />

        {/* Selection indicator */}
        <SelectionIndicator visible={isSelected} size="md" className="z-10" />
      </div>

      {/* Text editor area - compact footer, separate click zone with stopPropagation */}
      {/* onPointerDown stopPropagation prevents drag-and-drop from capturing text selection */}
      <div
        className="flex-shrink-0 w-full p-2 border-t border-border bg-surface-1"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          if (!isEditingText) setIsEditingText(true);
        }}
      >
        {isEditingText ? (
          <div className="space-y-1">
            <textarea
              ref={textareaRef}
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              onBlur={handleTextBlur}
              onKeyDown={handleKeyDown}
              className={cn(
                'w-full min-h-[60px] p-2 text-sm border rounded bg-surface-1 text-foreground resize-vertical',
                'focus:outline-none focus:ring-2 focus:ring-primary',
                isOverLimit && 'border-danger focus:ring-danger'
              )}
              placeholder="Add description..."
              disabled={isSaving}
            />
            <div className="flex justify-between items-center text-xs">
              <span className={cn('text-muted', isOverLimit && 'text-danger')}>
                {charCount} / {MAX_TEXT_LENGTH}
              </span>
              <span className="text-muted">
                Enter to save • Esc to cancel
              </span>
            </div>
          </div>
        ) : (
          <div
            className={cn(
              'w-full min-h-[40px] p-2 text-sm rounded cursor-text',
              'hover:bg-surface-3 transition-colors',
              'line-clamp-2',
              !textValue && 'text-muted italic'
            )}
          >
            {textValue || 'Click to add text...'}
          </div>
        )}
      </div>

      {/* Note: Drag-and-drop reordering is handled by PhotoCollection2 layout
          when reorderMode={true}. The entire photo item becomes draggable.
          No custom drag-drop code needed here. */}
    </div>
  );
}
